import * as THREE from 'three/webgpu';

const BLUE = new THREE.Color(0x168bda);
const PALE_BLUE = new THREE.Color(0x61bce8);
const BLACK = new THREE.Color(0x080b0d);
const CLEAR = new THREE.Color(0xd7ebee);

const clamp01 = (value) => Math.min(Math.max(value, 0), 1);
const smooth = (value) => {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
};

function boundsOf(meshes) {
  const bounds = new THREE.Box3();
  meshes.forEach((mesh) => bounds.expandByObject(mesh, true));
  return bounds;
}

function supportHeightAt(meshes, position, startY) {
  const raycaster = new THREE.Raycaster(
    new THREE.Vector3(position.x, startY, position.z),
    new THREE.Vector3(0, -1, 0),
    0,
    0.4,
  );
  meshes.forEach((mesh) => mesh.updateWorldMatrix(true, false));
  return raycaster.intersectObjects(meshes, false)[0]?.point.y;
}

function createLabel(text) {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 144;
  const context = canvas.getContext('2d');
  context.fillStyle = 'rgba(9, 17, 22, 0.88)';
  context.roundRect(8, 8, 496, 128, 20);
  context.fill();
  context.strokeStyle = 'rgba(87, 204, 232, 0.48)';
  context.lineWidth = 3;
  context.stroke();
  context.fillStyle = '#ecf8fb';
  context.font = '600 48px Inter, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, 256, 72);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  }));
  sprite.scale.set(0.15, 0.042, 1);
  sprite.renderOrder = 20;
  return sprite;
}

function createTubeMarker() {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  context.strokeStyle = 'rgba(83, 218, 242, 0.96)';
  context.lineWidth = 8;
  context.beginPath();
  context.arc(64, 64, 45, 0, Math.PI * 2);
  context.stroke();
  context.strokeStyle = 'rgba(232, 252, 255, 0.92)';
  context.lineWidth = 2;
  context.beginPath();
  context.arc(64, 64, 35, 0, Math.PI * 2);
  context.stroke();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  }));
  sprite.renderOrder = 19;
  return sprite;
}

function addMesh(parent, geometry, material, name, position = [0, 0, 0]) {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.position.set(...position);
  parent.add(mesh);
  return mesh;
}

function createBottleRack(parent, bounds, name) {
  const size = bounds.getSize(new THREE.Vector3());
  const center = bounds.getCenter(new THREE.Vector3());
  const footprint = Math.max(size.x, size.z);
  const baseWidth = Math.max(size.x * 1.75, footprint * 1.45);
  const baseDepth = Math.max(size.z * 1.75, footprint * 1.45);
  const baseThickness = Math.max(size.y * 0.045, 0.005);
  const baseTop = bounds.min.y - Math.max(size.y * 0.025, 0.002);
  const ringY = bounds.min.y + size.y * 0.16;
  const postHeight = Math.max(ringY - baseTop, baseThickness * 2);
  const material = new THREE.MeshStandardMaterial({
    color: 0xe8ecec,
    roughness: 0.62,
    metalness: 0.16,
  });
  const rack = new THREE.Group();
  rack.name = name;
  parent.add(rack);

  addMesh(
    rack,
    new THREE.BoxGeometry(baseWidth, baseThickness, baseDepth),
    material,
    `${name}_Base`,
    [center.x, baseTop - baseThickness * 0.5, center.z],
  );

  const ring = addMesh(
    rack,
    new THREE.TorusGeometry(footprint * 0.62, Math.max(footprint * 0.055, 0.0022), 8, 28),
    material,
    `${name}_Socket`,
    [center.x, ringY, center.z],
  );
  ring.rotation.x = Math.PI / 2;

  const postGeometry = new THREE.CylinderGeometry(
    Math.max(footprint * 0.035, 0.0018),
    Math.max(footprint * 0.045, 0.0022),
    postHeight,
    8,
  );
  const postRadius = footprint * 0.52;
  for (let index = 0; index < 4; index += 1) {
    const angle = Math.PI * 0.25 + index * Math.PI * 0.5;
    addMesh(
      rack,
      postGeometry,
      material,
      `${name}_Post_${index + 1}`,
      [
        center.x + Math.cos(angle) * postRadius,
        baseTop + postHeight * 0.5,
        center.z + Math.sin(angle) * postRadius,
      ],
    );
  }
  return rack;
}

function configureClearGlass(material) {
  material.color?.set(0xf7fafb);
  material.emissive?.set(0x000000);
  if ('emissiveIntensity' in material) material.emissiveIntensity = 0;
  material.transparent = true;
  material.opacity = 0.3;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;
  if ('transmission' in material) material.transmission = 0.86;
  if ('roughness' in material) material.roughness = 0.08;
  if ('metalness' in material) material.metalness = 0;
  if ('thickness' in material) material.thickness = 0.012;
  if ('ior' in material) material.ior = 1.45;
  material.needsUpdate = true;
  return material;
}

function quadraticPoint(start, control, end, progress) {
  const t = clamp01(progress);
  const inverse = 1 - t;
  return start.clone().multiplyScalar(inverse * inverse)
    .addScaledVector(control, 2 * inverse * t)
    .addScaledVector(end, t * t);
}

function samplePath(time, points) {
  for (let index = 0; index < points.length - 1; index += 1) {
    const [startTime, start] = points[index];
    const [endTime, end] = points[index + 1];
    if (time <= endTime) return start.clone().lerp(end, smooth((time - startTime) / (endTime - startTime)));
  }
  return points.at(-1)[1].clone();
}

function cloneWorldMeshes(meshes, parent, offset, suffix) {
  return meshes.map((source) => {
    source.updateMatrixWorld(true);
    const clone = source.clone();
    clone.name = `${source.name}_${suffix}`;
    clone.material = Array.isArray(source.material)
      ? source.material.map((material) => material.clone())
      : source.material.clone();
    source.matrixWorld.decompose(clone.position, clone.quaternion, clone.scale);
    clone.position.add(offset);
    parent.add(clone);
    return clone;
  });
}

export class ChemicalExperiment {
  constructor({ modelMeshes, robotArmIK }) {
    this.root = new THREE.Group();
    this.root.name = 'Chemical_Reaction_Effects';
    this.root.visible = false;
    this.duration = 32;
    this.elapsed = 0;
    this.playing = true;
    this.onStateChange = null;
    this.robotArmIK = robotArmIK;
    if (!this.robotArmIK) throw new Error('Chemical experiment requires the ABB IK rig.');

    // Keep the authored workstation and shelf; stage cloned consumables in their shelf slots.
    const assetPrefixes = [
      'Desktop',
      'Main_Shelves_001',
      'Powder_Adding_Module',
    ];
    this.sourceTube = modelMeshes.find((mesh) => mesh.name === 'Reactiontube_1mL');
    this.assetMeshes = modelMeshes.filter(
      (mesh) => mesh !== this.sourceTube && assetPrefixes.some((prefix) => mesh.name.startsWith(prefix)),
    );
    this.powderMeshes = modelMeshes.filter((mesh) => mesh.name.startsWith('Powder_Adding_Module'));
    this.shelfMeshes = modelMeshes.filter((mesh) => mesh.name.startsWith('Main_Shelves_001'));
    this.staticArmMeshes = modelMeshes.filter((mesh) => mesh.name.startsWith('机械臂'));
    const copperBottleSources = modelMeshes.filter((mesh) => mesh.name.startsWith('Bottle_8ml__'));
    const sodiumBottleSources = modelMeshes.filter((mesh) => mesh.name.startsWith('Bottle_8ml_001__'));
    const bottleTraySources = modelMeshes.filter(
      (mesh) => mesh.name.startsWith('Sharp_Fusion_1_013_001_001'),
    );
    if (
      !this.sourceTube
      || !this.powderMeshes.length
      || !this.shelfMeshes.length
      || !copperBottleSources.length
      || !sodiumBottleSources.length
      || !bottleTraySources.length
    ) {
      throw new Error('Chemical experiment assets are incomplete in labgym_web.glb.');
    }

    const tubeBounds = boundsOf([this.sourceTube]);
    const tubeSize = tubeBounds.getSize(new THREE.Vector3());
    this.tubeHeight = tubeSize.y;
    const shelfBounds = boundsOf(this.shelfMeshes);
    const staticArmBounds = boundsOf(this.staticArmMeshes);
    this.robotArmIK.configureExternalAlignment(staticArmBounds);
    const tubeSourceCenter = tubeBounds.getCenter(new THREE.Vector3());
    const tubeRackBaseThickness = Math.max(tubeSize.y * 0.045, 0.005);
    const tubeRackGap = Math.max(tubeSize.y * 0.025, 0.002);
    const tubeSupportY = supportHeightAt(
      this.shelfMeshes,
      tubeSourceCenter,
      tubeBounds.min.y + 0.02,
    );
    if (tubeSupportY === undefined) {
      throw new Error('Could not find a shelf surface below the reaction tube.');
    }
    const tubeBottomY = tubeSupportY + 0.001 + tubeRackBaseThickness + tubeRackGap;
    this.homePosition = tubeSourceCenter.clone().setY(tubeBottomY + tubeSize.y * 0.5);
    this.tube = this.sourceTube.clone();
    this.tube.name = 'Reactiontube_1mL_Experiment';
    this.tube.material = Array.isArray(this.sourceTube.material)
      ? this.sourceTube.material.map((material) => material.clone())
      : this.sourceTube.material.clone();
    const tubeMaterials = Array.isArray(this.tube.material) ? this.tube.material : [this.tube.material];
    tubeMaterials.forEach(configureClearGlass);
    this.sourceTube.getWorldPosition(this.tube.position);
    this.sourceTube.getWorldQuaternion(this.tube.quaternion);
    this.sourceTube.getWorldScale(this.tube.scale);
    this.tube.position.add(this.homePosition.clone().sub(tubeSourceCenter));
    this.tubeInitialPosition = this.tube.position.clone();
    this.root.add(this.tube);

    const copperSourceBounds = boundsOf(copperBottleSources);
    const sodiumSourceBounds = boundsOf(sodiumBottleSources);
    const traySourceBounds = boundsOf(bottleTraySources);
    const traySourceCenter = traySourceBounds.getCenter(new THREE.Vector3());
    const traySupportY = supportHeightAt(
      this.shelfMeshes,
      traySourceCenter,
      traySourceBounds.min.y + 0.02,
    );
    if (traySupportY === undefined) {
      throw new Error('Could not find a shelf surface below the reagent tray.');
    }
    const trayOffset = new THREE.Vector3(0, traySupportY + 0.001 - traySourceBounds.min.y, 0);
    this.bottleTrayMeshes = cloneWorldMeshes(
      bottleTraySources,
      this.root,
      trayOffset,
      'Experiment_Bottle_Tray',
    );
    const trayBounds = boundsOf(this.bottleTrayMeshes);
    const trayCenter = trayBounds.getCenter(new THREE.Vector3());
    const traySize = trayBounds.getSize(new THREE.Vector3());
    const copperSourceCenter = copperSourceBounds.getCenter(new THREE.Vector3());
    const sodiumSourceCenter = sodiumSourceBounds.getCenter(new THREE.Vector3());
    const copperSlot = new THREE.Vector3(
      trayCenter.x - traySize.x * 0.2,
      copperSourceCenter.y + trayOffset.y,
      trayCenter.z,
    );
    const sodiumSlot = new THREE.Vector3(
      trayCenter.x + traySize.x * 0.2,
      sodiumSourceCenter.y + trayOffset.y,
      trayCenter.z,
    );
    this.copperBottleMeshes = cloneWorldMeshes(
      copperBottleSources,
      this.root,
      copperSlot.clone().sub(copperSourceCenter),
      'CuSO4_Experiment',
    );
    this.sodiumBottleMeshes = cloneWorldMeshes(
      sodiumBottleSources,
      this.root,
      sodiumSlot.clone().sub(sodiumSourceCenter),
      'NaOH_Experiment',
    );
    const copperBounds = boundsOf(this.copperBottleMeshes);
    const sodiumBounds = boundsOf(this.sodiumBottleMeshes);
    const copperCenter = copperBounds.getCenter(new THREE.Vector3());
    const stagedTubeBounds = new THREE.Box3(
      this.homePosition.clone().addScaledVector(tubeSize, -0.5),
      this.homePosition.clone().addScaledVector(tubeSize, 0.5),
    );
    this.tubeRack = createBottleRack(this.root, stagedTubeBounds, 'Reaction_Tube_Rack');
    const powderBounds = boundsOf(this.powderMeshes);
    const powderSize = powderBounds.getSize(new THREE.Vector3());
    const powderCenter = powderBounds.getCenter(new THREE.Vector3());
    const openingY = powderBounds.min.y + powderSize.y * 0.3;
    // The usable feed port is on the right half of the front opening.
    const openingX = powderCenter.x + powderSize.x * 0.26;
    const frontZ = powderBounds.max.z;
    this.powderTarget = new THREE.Vector3(
      openingX,
      openingY,
      frontZ - this.tubeHeight * 0.35,
    );
    this.powderEntrance = new THREE.Vector3(
      openingX,
      openingY,
      frontZ + this.tubeHeight * 0.45,
    );
    this.powderApproach = this.powderEntrance.clone().add(
      new THREE.Vector3(0, 0, Math.max(powderSize.z * 0.13, this.tubeHeight * 3.5)),
    );
    const transferY = this.homePosition.y + this.tubeHeight * 2.2;
    this.homeLift = this.homePosition.clone().setY(transferY);
    this.powderHighApproach = this.powderApproach.clone().setY(transferY);
    const shelfExitZ = shelfBounds.min.z - Math.max(this.tubeHeight * 2.4, 0.07);
    this.shelfExit = this.homeLift.clone().setZ(shelfExitZ);

    for (const mesh of this.copperBottleMeshes.concat(this.sodiumBottleMeshes)) {
      if (!mesh.name.includes('__glass')) continue;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const clearMaterials = materials.map((material) => configureClearGlass(material.clone()));
      mesh.material = Array.isArray(mesh.material) ? clearMaterials : clearMaterials[0];
    }

    const solutionMaterial = new THREE.MeshPhysicalMaterial({
      color: BLUE,
      roughness: 0.18,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.tubeEffects = new THREE.Group();
    this.tubeEffects.name = 'Reaction_Tube_Effects';
    this.root.add(this.tubeEffects);
    this.tubeLabel = createLabel('反应管');
    this.tubeLabel.name = 'Reaction_Tube_Label';
    this.tubeLabel.position.set(0, tubeSize.y * 1.25, 0);
    this.tubeLabel.scale.set(0.15, 0.042, 1);
    this.tubeEffects.add(this.tubeLabel);
    this.tubeMarker = createTubeMarker();
    this.tubeMarker.name = 'Reaction_Tube_Marker';
    this.tubeMarker.position.set(0, tubeSize.y * 0.34, 0);
    this.markerSize = Math.max(tubeSize.x * 2.8, tubeSize.y * 0.72, 0.045);
    this.tubeMarker.scale.set(this.markerSize, this.markerSize, 1);
    this.tubeEffects.add(this.tubeMarker);
    this.solution = addMesh(
      this.tubeEffects,
      new THREE.CylinderGeometry(tubeSize.x * 0.32, tubeSize.x * 0.27, tubeSize.y * 0.43, 20),
      solutionMaterial,
      'CuSO4_Reaction_Solution',
      [0, -tubeSize.y * 0.18, 0],
    );
    this.solutionMaterial = solutionMaterial;

    this.transferEnd = this.homePosition.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.58, 0));
    this.transferStart = this.transferEnd.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.7, 0));
    this.transferControl = this.transferStart.clone().lerp(this.transferEnd, 0.5);
    this.transferDrops = [];
    for (let index = 0; index < 3; index += 1) {
      const drop = addMesh(
        this.root,
        new THREE.SphereGeometry(Math.max(tubeSize.x * 0.3, 0.0032), 14, 10),
        new THREE.MeshPhysicalMaterial({
          color: BLUE,
          roughness: 0.12,
          transparent: true,
          opacity: 0.88,
          depthWrite: false,
        }),
        `CuSO4_Transfer_Drop_${index}`,
      );
      drop.visible = false;
      this.transferDrops.push(drop);
    }

    this.precipitateMaterial = new THREE.MeshStandardMaterial({ color: PALE_BLUE, roughness: 0.88 });
    this.flocs = [];
    for (let index = 0; index < 8; index += 1) {
      const angle = index * 2.39996;
      const radius = tubeSize.x * (0.07 + (index % 3) * 0.07);
      const baseY = -tubeSize.y * (0.28 - (index % 4) * 0.035);
      const floc = addMesh(
        this.tubeEffects,
        new THREE.DodecahedronGeometry(tubeSize.x * (0.045 + (index % 2) * 0.012), 0),
        this.precipitateMaterial,
        `CuOH2_Floc_${index}`,
        [Math.cos(angle) * radius, baseY, Math.sin(angle) * radius],
      );
      floc.userData.baseY = baseY;
      floc.scale.setScalar(0);
      this.flocs.push(floc);
    }

    const copperSize = copperBounds.getSize(new THREE.Vector3());
    this.copperLiquid = addMesh(
      this.root,
      new THREE.CylinderGeometry(copperSize.x * 0.27, copperSize.x * 0.27, copperSize.y * 0.38, 18),
      new THREE.MeshPhysicalMaterial({ color: BLUE, transparent: true, opacity: 0.76, roughness: 0.2 }),
      'CuSO4_Bottle_Content',
      [copperBounds.getCenter(new THREE.Vector3()).x, copperBounds.min.y + copperSize.y * 0.31, copperBounds.getCenter(new THREE.Vector3()).z],
    );

    const sodiumSize = sodiumBounds.getSize(new THREE.Vector3());
    const sodiumCenter = sodiumBounds.getCenter(new THREE.Vector3());
    this.sodiumChunks = [];
    for (let index = 0; index < 5; index += 1) {
      const chunk = addMesh(
        this.root,
        new THREE.BoxGeometry(sodiumSize.x * 0.17, sodiumSize.x * 0.14, sodiumSize.x * 0.16),
        new THREE.MeshStandardMaterial({ color: 0xf1f4f1, roughness: 0.9 }),
        `NaOH_Chunk_${index}`,
        [
          sodiumCenter.x + ((index % 2) - 0.5) * sodiumSize.x * 0.22,
          sodiumBounds.min.y + sodiumSize.y * (0.18 + Math.floor(index / 2) * 0.075),
          sodiumCenter.z + ((index % 3) - 1) * sodiumSize.z * 0.12,
        ],
      );
      chunk.rotation.set(index * 0.42, index * 0.67, index * 0.29);
      this.sodiumChunks.push(chunk);
    }

    const copperLabel = createLabel('CuSO₄(aq)');
    copperLabel.position.copy(copperBounds.getCenter(new THREE.Vector3())).setY(copperBounds.max.y + 0.078);
    this.root.add(copperLabel);
    const sodiumLabel = createLabel('NaOH(s)');
    sodiumLabel.position.copy(sodiumBounds.getCenter(new THREE.Vector3())).setY(sodiumBounds.max.y + 0.035);
    this.root.add(sodiumLabel);

    this.effectMeshes = [];
    this.root.traverse((object) => {
      if (object.isMesh) this.effectMeshes.push(object);
    });
    this.meshes = this.assetMeshes.concat(this.effectMeshes, this.robotArmIK.meshes);

    this.path = [
      [0, this.homePosition],
      [10.8, this.homePosition],
      [11.6, this.homeLift],
      [12.4, this.shelfExit],
      [13.6, this.powderHighApproach],
      [14.4, this.powderApproach],
      [15.2, this.powderEntrance],
      [15.9, this.powderTarget],
      [18, this.powderTarget],
      [18.7, this.powderEntrance],
      [19.5, this.powderApproach],
      [20.7, this.powderHighApproach],
      [21.7, this.shelfExit],
      [22.5, this.homeLift],
      [23.4, this.homePosition],
      [32, this.homePosition],
    ];

    this.armRestTarget = this.robotArmIK.getExternalRestTargetWorld();
    this.armCopperApproach = copperCenter.clone().setY(copperBounds.max.y + this.tubeHeight * 1.35);
    this.armCopperPickup = copperCenter.clone().setY(copperBounds.max.y + this.tubeHeight * 0.45);
    this.armTubeApproach = this.homePosition.clone().add(new THREE.Vector3(0, this.tubeHeight * 1.45, 0));
    this.armTubeGrip = this.homePosition.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.58, 0));
    // Keep the completed-demo pose above the tube rack instead of returning to
    // the authored rest target, which places the lower links too close to the floor.
    this.armEndTarget = this.armTubeApproach.clone().add(
      new THREE.Vector3(0, Math.max(this.tubeHeight * 0.45, 0.025), 0),
    );
    const rackClearanceZ = shelfBounds.min.z - Math.max(this.tubeHeight * 1.8, 0.055);
    this.armCopperExit = this.armCopperApproach.clone().setZ(rackClearanceZ);
    this.armTubeExit = this.armTubeApproach.clone().setZ(rackClearanceZ);
    this.armPath = [
      [0, this.armRestTarget],
      [0.8, this.armRestTarget],
      [2.1, this.armCopperExit],
      [2.8, this.armCopperApproach],
      [3.4, this.armCopperPickup],
      [4, this.armCopperPickup],
      [4.6, this.armCopperApproach],
      [5.3, this.armCopperExit],
      [6.2, this.armTubeExit],
      [6.8, this.armTubeApproach],
      [7.3, this.armTubeGrip],
      [8.4, this.armTubeGrip],
      [8.9, this.armTubeApproach],
      [9.5, this.armTubeExit],
      [10.1, this.armTubeApproach],
      [10.8, this.armTubeGrip],
    ];
    this.armReturnPath = [
      [23.4, this.armTubeGrip],
      [24, this.armTubeApproach],
      [24.8, this.armTubeExit],
      [26.2, this.armEndTarget],
      [32, this.armEndTarget],
    ];
    this.applyState();
  }

  get phase() {
    if (this.elapsed < 9.2) return { index: 0, label: '移取 CuSO₄ 溶液', detail: '机械臂到试剂瓶吸液，停顿后返回试管加液' };
    if (this.elapsed < 18.2) return { index: 1, label: 'NaOH 加料', detail: '机械臂夹取试管，从下方开口送入加粉模块' };
    if (this.elapsed < 24.2) return { index: 2, label: '试管复位', detail: '机械臂取出试管并放回原试管架，等待反应' };
    if (this.elapsed < 28.2) return { index: 2, label: '生成 Cu(OH)₂', detail: '试管底部形成少量蓝色絮状沉淀' };
    if (this.elapsed < 31.5) return { index: 3, label: '沉淀转化', detail: 'NaOH 溶解放热，沉淀逐渐变黑，溶液褪色' };
    return { index: 4, label: '反应完成', detail: '少量黑色 CuO 沉淀与无色上清液' };
  }

  setVisible(visible) {
    this.root.visible = visible;
    if (visible) this.sourceTube.visible = false;
    if (!visible) this.restoreTube();
  }

  setPlaying(playing) {
    if (playing && this.elapsed >= this.duration) this.elapsed = 0;
    this.playing = playing;
    this.onStateChange?.();
  }

  toggle() {
    this.setPlaying(!this.playing);
  }

  reset() {
    this.elapsed = 0;
    this.playing = true;
    this.robotArmIK.reset();
    this.applyState();
    this.onStateChange?.();
  }

  seek(time) {
    this.elapsed = THREE.MathUtils.clamp(time, 0, this.duration);
    this.playing = false;
    this.robotArmIK.reset();
    this.applyState();
    this.onStateChange?.();
  }

  update(delta) {
    if (!this.playing || !this.root.visible) return;
    this.elapsed = Math.min(this.elapsed + Math.min(delta, 0.05), this.duration);
    if (this.elapsed >= this.duration) this.playing = false;
    this.applyState();
    this.onStateChange?.();
  }

  moveTubeTo(worldCenter) {
    this.tube.position.copy(this.tubeInitialPosition).add(worldCenter.clone().sub(this.homePosition));
    this.tube.updateMatrixWorld(true);
    this.tubeEffects.position.copy(worldCenter);
  }

  restoreTube() {
    this.tube.position.copy(this.tubeInitialPosition);
    this.tube.updateMatrixWorld(true);
  }

  applyState() {
    const time = this.elapsed;
    const tubePosition = samplePath(time, this.path);
    this.moveTubeTo(tubePosition);

    let armTarget;
    if (time <= 10.8) {
      armTarget = samplePath(time, this.armPath);
    } else if (time <= 23.4) {
      armTarget = tubePosition.clone().add(new THREE.Vector3(0, this.tubeHeight * 0.58, 0));
    } else {
      armTarget = samplePath(time, this.armReturnPath);
    }
    const wristRoll = time >= 10.8 && time <= 23.4
      ? Math.sin((time - 10.8) * 0.72) * 0.28
      : 0;
    this.robotArmIK.setWorldTarget(armTarget, wristRoll);

    const solutionAmount = smooth((time - 7.2) / 1.1);
    const fading = smooth((time - 28.2) / 3.3);
    this.solutionMaterial.opacity = solutionAmount * (0.76 - fading * 0.58);
    this.solutionMaterial.color.copy(BLUE).lerp(CLEAR, fading);

    const precipitation = smooth((time - 24.2) / 3.2);
    const blackening = smooth((time - 28.2) / 3.1);
    this.precipitateMaterial.color.copy(PALE_BLUE).lerp(BLACK, blackening);
    this.flocs.forEach((floc, index) => {
      const stagger = clamp01(precipitation * 1.25 - index * 0.055);
      floc.scale.setScalar(smooth(stagger) * (0.78 + (index % 3) * 0.08));
      const settling = blackening * this.tubeHeight * (0.012 + index * 0.0025);
      floc.position.y = floc.userData.baseY - settling;
    });

    const transferActive = time >= 7.15 && time <= 8.45;
    this.transferDrops.forEach((drop, index) => {
      const progress = clamp01((time - 7.15) / 0.85 * 1.45 - index * 0.2);
      drop.visible = transferActive && progress > 0.015 && progress < 0.995;
      drop.position.copy(quadraticPoint(this.transferStart, this.transferControl, this.transferEnd, progress));
    });

    const bottleDraw = smooth((time - 3.2) / 0.9);
    this.copperLiquid.scale.y = 1 - bottleDraw * 0.08;

    const markerPulse = 1 + Math.sin(time * 6.5) * 0.08;
    this.tubeMarker.scale.set(this.markerSize * markerPulse, this.markerSize * markerPulse, 1);

  }

  setWireframe(enabled) {
    this.effectMeshes.forEach((mesh) => {
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach((material) => {
        material.wireframe = enabled;
        material.needsUpdate = true;
      });
    });
  }
}
